// Hard onboarding gate. While a non-admin user has incomplete mandatory
// courses/quizzes, the platform is locked to the Learning section: this
// component (a) polls /learning/me/required into the shared store on every
// navigation, and (b) redirects any non-Learning app route back to
// /workspace/learning. The rail (PrimarySidebar) reads the same store and
// hides every other section. Applies to EVERYONE incl. admins (product
// decision 2026-05-29 — onboarding is mandatory for all). Fail-open: if the
// status hasn't loaded or the API errored, we never redirect.
//
// This is the ENFORCING counterpart to the soft RequiredLearningGate
// banner (which now just reads the same store for its nudge text).
import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useGuestStore } from '../stores/guestStore'
import { useServerStore } from '../shell/stores/serverStore'
import { useMandatoryLearningStore } from '../stores/mandatoryLearningStore'

// Routes that render without the authenticated app shell (or pre-auth) —
// never redirect away from these, even when blocked.
const UNGATED_PREFIXES = [
  '/login', '/register', '/activate', '/reset-password',
  '/invite', '/join', '/call', '/book', '/booking', '/form', '/embed', '/_/',
]

export default function MandatoryLearningGate() {
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  // OSPROD-178: a guest is someone who joined a specific room via an
  // invite link (/join/:code → guest:token session). They are NOT org
  // members and must never be subjected to mandatory onboarding — the
  // invite's whole point is fast access to the invited room. Reliable
  // across reloads: guestStore rehydrates isGuest from localStorage, and
  // the synthetic guest user carries role 'guest'.
  const isGuest = useGuestStore((s) => s.isGuest)
  const isGuestSession = isGuest || user?.role === 'guest'
  const { activeServerId, servers } = useServerStore()
  const blocked = useMandatoryLearningStore((s) => s.blocked)
  const loaded = useMandatoryLearningStore((s) => s.loaded)
  const refresh = useMandatoryLearningStore((s) => s.refresh)

  const serverId =
    activeServerId ??
    (user?.settings?.lastActiveServerId
      ? servers.find((s) => s.id === (user!.settings!.lastActiveServerId as string))?.id
      : undefined) ??
    servers[0]?.id

  // Poll on mount + every navigation (pathname OR search) so finishing the
  // course — which only changes the ?lesson/?course query params — clears
  // the gate without a hard reload. Runs for — and now redirects — ALL
  // authenticated users, admins included. EXCEPT guests: we never poll for
  // them and force the shared store to "not blocked" so the gate, the rail
  // (PrimarySidebar hides sections off the same store) and the soft banner
  // all treat a guest as unblocked from the single point of truth.
  useEffect(() => {
    if (!user) return
    if (isGuestSession) {
      useMandatoryLearningStore.setState({ blocked: false, items: [], loaded: true })
      return
    }
    if (!serverId) return
    void refresh(serverId)
  }, [user, isGuestSession, serverId, location.pathname, location.search, refresh])

  // Belt-and-suspenders: never redirect a guest even if the store were to
  // report blocked from a stale poll before the effect above ran.
  if (isGuestSession) return null
  if (!user || !loaded || !blocked) return null

  const p = location.pathname
  const onLearning = p.startsWith('/workspace/learning')
  const ungated = UNGATED_PREFIXES.some((x) => p.startsWith(x))
  if (onLearning || ungated) return null

  return <Navigate to="/workspace/learning" replace />
}
